import {Component, HostListener, OnInit} from '@angular/core';
import * as _ from 'lodash';
import { Sidenavaction } from 'src/assets/js/application/sidenavaction';
import CustomerPondModel from 'src/app/services/customerPond.model';
import { CustomerPondService } from 'src/app/services/customerPond.service';
import { FeedLogService } from 'src/app/services/feedLog.service';
import FeedLogModel from 'src/app/services/feedLog.model';
import { FarmSiteService } from 'src/app/services/farmSite.service';
import FarmSiteModel from 'src/app/services/farmSite.model';
import { CommonMethodsService } from 'src/app/services/commonMethods.service';
import { LocationStrategy } from '@angular/common';
import { PreviousRouteService } from 'src/app/services/previousRouteService';
import {BatchLoader} from "../../../../Utils/batch-loader.util";

declare var $: any;

/**
 * @component TotalAreaListComponent
 * @selector app-total-area-list
 * @implements {OnInit}
 *
 * @description
 * The `TotalAreaListComponent` is responsible for displaying a comprehensive list of all active ponds (total area)
 * across the farm business. It serves as a central dashboard view for monitoring pond status, feed logs, and
 * other key metrics.
 *
 * ## Key Responsibilities:
 * 1. **Data Aggregation**: Fetches and aggregates pond data from all farm sites associated with the user's business.
 * 2. **Filtering & Search**: Provides client-side filtering capabilities to search ponds by name, section, location, or farm name.
 * 3. **Sorting**: Allows users to sort the pond list by Farm Name, Section Name, Pond Name, or Location Name.
 * 4. **Pagination/Infinite Scroll**: Implements a batch loading mechanism to handle large datasets efficiently.
 * 5. **Detail View**: Allows users to view detailed feed logs and ABW/FCR metrics for a specific pond in a modal.
 * 6. **Role-Based Access**: Adapts data retrieval based on the logged-in user's role (Farmer, Farm Manager, Business Admin, Provider).
 *
 * ## User Roles & Permissions:
 * - **Farm Category Users**:
 *   - **Business Admin / Farmer**: Access to all ponds across all farm sites in their business.
 *   - **Farm Manager**: Restricted access to ponds in farm sites assigned to them.
 * - **Provider Category Users**:
 *   - **Business Admin**: Access to ponds in farm sites associated with the provider business.
 * - **Other Categories**:
 *   - **Employee**: Access to all farm sites (typically for internal admin/support).
 *
 * ## LocalStorage Dependencies:
 * - `loginPersonType`: User role.
 * - `loginBusinessCategory`: Business context.
 * - `businessID`: ID of the logged-in user's business.
 * - `personID`: ID of the logged-in user.
 * - `loginBusinessName`: Used for display.
 *
 * ## Data Flow:
 * 1. **Initialization**: `ngOnInit` determines user context and fetches relevant farm sites.
 * 2. **Data Fetching**: `customerPondService.getPondsByFarmsites` is called with the list of farm site IDs.
 * 3. **Display**: Data is loaded into `customerPonds` (display list) via `BatchLoader`.
 * 4. **Filtering**: `searchAreaList` filters the `originalpondList` based on search input.
 * 5. **Detail View**: `getAllTotalAbwfcrList` fetches feed logs for a selected pond and opens a modal.
 *
 * ## UI State Management:
 * - **Loading**: `pgLoad`, `listLoader` manage loading indicators.
 * - **Sorting**: Flags like `pondfarmAsc`, `pondnameAsc` manage sort direction.
 * - **Pagination**: `BatchLoader` handles infinite scroll pagination.
 */
@Component({
  selector: 'app-total-area-list',
  templateUrl: './total-area-list.component.html',
  styleUrls: ['./total-area-list.component.scss']
})
export class TotalAreaListComponent implements OnInit {
  /** @type {Array<FarmSiteModel>} List of farm sites associated with the user. */
  public farmSites: Array<FarmSiteModel>;

  /** @type {FarmSiteModel} Single farm site model (unused in current logic). */
  public farmSite: FarmSiteModel;

  /** @type {Array<CustomerPondModel>} The list of customer ponds currently displayed in the UI. */
  public customerPonds: Array<CustomerPondModel>;

  /** @type {CustomerPondModel} Single customer pond model (unused in current logic). */
  public customerPond: CustomerPondModel;

  /** @type {FeedLogModel} Feed log model for detailed view. */
  public feedlogs: FeedLogModel;

  /** @type {boolean} Flag for page loading state. */
  pgLoad = false;

  /** @type {string} Name of the selected pond for detail view. */
  pondName: string;

  // pagination sorting
  /** @type {number} Current page number. Default 1. */
  currentPageNumberum: number = 1;

  /** @type {number} Items per page. Default 15. */
  noOfItemsPerPage: number = 15;

  /* End pagination, sorting, filter*/
  /** @type {boolean} Flag for sorting by Farm Name. */
  pondfarmAsc: boolean;

  /** @type {any} Original list of ponds before filtering. */
  originalpondList: any;

  /** @type {boolean} Flag for sorting by Section Name. */
  pondsectionnameAsc: boolean;

  /** @type {boolean} Flag for sorting by Location Name. */
  pondlocationnameAsc: boolean;

  /** @type {boolean} Flag for sorting by Pond Name. */
  pondnameAsc: boolean;

  /** @type {string} Search keyword. */
  search: string;

  /** @type {string} Role of the logged-in user. */
  public loginPersonType: string;

  /** @type {string} Name of the logged-in user's business. */
  public loginUserBusinessName: string;

  /** @type {string} Category of the logged-in user's business. */
  public loginUserBusinessCategory: string;

  /** @type {boolean} Flag controlling list loader spinner. */
  public listLoader = false;              // Show listLoader spinner

  /** @type {number} Start index for batch loading. */
  public startIndex = 0;             // Tracks how much has been loaded

  /** @type {boolean} Flag indicating if filtering is active. */
  public isFiltering: boolean = false;

  /** @type {boolean} Flag indicating if list is empty. */
  public noList: boolean = false;

  /** @type {boolean} Flag to enable/disable page loader (unused). */
  public enableDisablePageLoader: boolean = false;

  /** @type {any} Timeout for search debounce (unused). */
  searchTimeout: any;

  /** @type {BatchLoader<any>} Batch loader utility instance. */
  private batchLoader: BatchLoader<any>;

  /** @type {any[]} Master list of customer ponds fetched from service. */
  customerPondsList: any[];

  /**
   * Constructor for TotalAreaListComponent.
   * Initializes services.
   *
   * @param {FarmSiteService} farmsiteEntityService - Service for farm site operations.
   * @param {CustomerPondService} customerPondService - Service for customer pond operations.
   * @param {FeedLogService} feedLogService - Service for feed logs.
   * @param {Sidenavaction} sidenavaction - Service for side navigation.
   * @param {CommonMethodsService} commonMethodsService - Service for common utility methods.
   * @param {LocationStrategy} locationStrategy - Service for interacting with the browser's URL.
   * @param {PreviousRouteService} previousRouteService - Service for tracking route history.
   *
   * @service FarmSiteService
   * @service CustomerPondService
   * @service FeedLogService
   * @service Sidenavaction
   * @service CommonMethodsService
   * @service LocationStrategy
   * @service PreviousRouteService
   */
  constructor(public farmsiteEntityService: FarmSiteService, public customerPondService: CustomerPondService, public feedLogService: FeedLogService, public sidenavaction: Sidenavaction, public commonMethodsService: CommonMethodsService,
    private locationStrategy: LocationStrategy, private previousRouteService: PreviousRouteService
  ) {
  }

  /**
   * @lifecycle ngOnInit
   * @async
   * @description
   * Lifecycle hook that is called after data-bound properties of a directive are initialized.
   * - Retrieves user context from `localStorage`.
   * - Sets up the previous route.
   * - Determines logic flow based on `loginBusinessCategory` and `loginPersonType`.
   * - Fetches farm sites and then ponds associated with those sites.
   * - Initializes the batch loader for pagination.
   *
   * @throws {Error} Captures and displays errors via `commonMethodsService` if initialization fails.
   */
  public async ngOnInit() {
    try {
      window.scrollTo(0, 0);
      this.isFiltering = false;
      this.customerPonds = [];
      this.customerPondsList = [];
      this.previousRouteService.setPreviousRoute(this.locationStrategy.path());
      // get person list based on login businessid
      // loginperson type details
      this.loginPersonType = localStorage.getItem('loginPersonType');
      // login person Business name
      this.loginUserBusinessName = localStorage.getItem('loginBusinessName');
      this.loginUserBusinessCategory = localStorage.getItem('loginBusinessCategory');
      let farmsiteIds = new Array();
      if ((this.loginPersonType == 'Farmer' || this.loginPersonType == 'Business Admin') && this.loginUserBusinessCategory == 'Farm') {
        let farmSiteModel = await this.farmsiteEntityService.getFarmeSitesByFarmsId(localStorage.getItem('businessID'));
        farmsiteIds = farmSiteModel.map(farmsite => farmsite.id);
        this.customerPonds = await this.customerPondService.getPondsByFarmsites(farmsiteIds);
        if (this.customerPonds !== undefined) {
          this.originalpondList = [...this.customerPonds];
          this.customerPonds = _.orderBy(this.customerPonds, ['createdAt'], ['desc']);
          this.pgLoad = true;
          this.customerPondsList = this.customerPonds;
        }
      } else if (this.loginPersonType == 'Farm Manager' && this.loginUserBusinessCategory == 'Farm') {
        let farmSiteModel = await this.farmsiteEntityService.getFarmeSitesByFarmManagerIdInFarmSpend(localStorage.getItem('businessID'), localStorage.getItem('personID'));
        farmsiteIds = farmSiteModel.map(farmsite => farmsite.id);
        this.customerPonds = await this.customerPondService.getPondsByFarmsites(farmsiteIds);
        if (this.customerPonds !== undefined) {
          this.originalpondList = [...this.customerPonds];
          this.customerPonds = _.orderBy(this.customerPonds, ['createdAt'], ['desc']);
          this.pgLoad = true;
          this.customerPondsList = [...this.customerPonds];
        }
      } else if (this.loginPersonType == 'Business Admin' && this.loginUserBusinessCategory == 'Provider') {
        let farmSiteModel = await this.farmsiteEntityService.getFarmeSitesBusinessDetailsByProviderId(localStorage.getItem('businessID'));
        farmsiteIds = farmSiteModel.map(farmsite => farmsite.id);
        this.customerPonds = await this.customerPondService.getPondsByFarmsites(farmsiteIds);
        if (this.customerPonds !== undefined) {
          this.originalpondList = [...this.customerPonds];
          this.customerPonds = _.orderBy(this.customerPonds, ['createdAt'], ['desc']);
          this.pgLoad = true;
          this.customerPondsList = [...this.customerPonds];
        }
      } else {
        let farmSiteModel = await this.farmsiteEntityService.getAllFarmSites();
        farmsiteIds = farmSiteModel.map(farmsite => farmsite.id);
        this.originalpondList = [...this.customerPonds];
        this.pgLoad = true;
        if (this.customerPonds !== undefined) {
          this.originalpondList = Object.assign({}, this.customerPonds);
          this.customerPonds = _.orderBy(this.customerPonds, ['createdAt'], ['desc']);
          this.customerPondsList = [...this.customerPonds];
        }
      }
      this.initializeBatchLoader(); // Reset instance
      this.batchLoader?.reset();     // Clear state
      await this.batchLoader?.loadNextBatch(true); // Load first batch
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  /**
   * @method onScroll
   * @description Host listener for window scroll events. Triggers `checkScrollAndLoad` to handle infinite scrolling.
   * @lifecycle HostListener
   */
  @HostListener('window:scroll', [])
  onScroll(): void {
    this.checkScrollAndLoad();
  }

  private scrollTimeout: any;

  /**
   * @method checkScrollAndLoad
   * @private
   * @description Debounced handler for scroll events. Checks if the user has scrolled near the bottom of the page.
   */
  private checkScrollAndLoad(): void {
    clearTimeout(this.scrollTimeout);
    this.scrollTimeout = setTimeout(() => {
      if ((window.innerHeight + window.scrollY) >= document.body.offsetHeight - 100 && !this.batchLoader?.isLoading()) {
        this.batchLoader?.loadNextBatch();
      }
    }, 50);
  }

  /**
   * @method initializeBatchLoader
   * @private
   * @description Initializes the `BatchLoader` utility with the master data list and callback functions for UI updates.
   */
  private initializeBatchLoader(): void {
    this.batchLoader = new BatchLoader<any>(
      () => this.isFiltering ? this.customerPonds : this.customerPondsList,
      (updatedList) => {
        this.customerPonds = updatedList;
        this.noList = updatedList.length === 0;
      },
      undefined, // <-- omit or pass undefined
      (loading) => this.listLoader = loading
    );
  }

  /**
   * @method getAllTotalAbwfcrList
   * @async
   * @description Fetches detailed feed logs and metrics for a specific pond.
   * Opens a modal to display the details.
   * @param {any} list - The pond object containing ID and culture data.
   * @param {string} pondName - The name of the pond.
   */
  async getAllTotalAbwfcrList(list: any, pondName) {
    try {
      const pondId = list.id;
      const cultureId = list && list.PondCultureData[0] && list.PondCultureData[0].id;
      this.pondName = pondName;
      if (pondId && cultureId) {
        $('#myModal').modal('show');
        this.feedlogs = await this.feedLogService.getPondsByFarmsites(pondId, cultureId);
      }
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  /**
   * @method searchAreaList
   * @description Filters the pond list based on the search input.
   * Matches against pond name, section name, village (location), and business name.
   * Resets the batch loader with the filtered list.
   */
  public searchAreaList() {
    try {
      const searchvalue = this.search?.toLowerCase() || '';
      if (!searchvalue) {
        this.customerPondsList = [...this.originalpondList]; // reset
      } else {
        this.customerPondsList = _.filter(this.originalpondList, (obj) => {
          return (
            obj?.name?.toLowerCase().includes(searchvalue) ||
            obj?.PondSection?.section_name?.toLowerCase().includes(searchvalue) ||
            obj?.FarmSiteDetails?.[0]?.address?.village?.toLowerCase().includes(searchvalue) ||
            obj?.FarmSiteDetails?.[0]?.businessID?.businessName?.toLowerCase().includes(searchvalue)
          );
        });
      }

      // Reset and load new filtered list
      this.batchLoader?.reset();
      this.batchLoader?.loadNextBatch(true);

    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  /**
   * @method sortingTanks
   * @description Sorts the displayed pond list based on the specified type.
   * Supports sorting by 'farm', 'sectionname', 'pondname', 'locationname'.
   * @param {string} type - The field to sort by.
   */
  public sortingTanks(type: string) {
    try {
      let sortblelist = this.search ? this.customerPondsList : this.originalpondList;

      const getValue = (item: any) => {
        switch (type) {
          case 'farm': return item?.FarmSiteDetails?.[0]?.businessID?.businessName || '';
          case 'sectionname': return item?.PondSection?.section_name || '';
          case 'pondname': return item?.name || '';
          case 'locationname': return item?.FarmSiteDetails?.[0]?.address?.village || '';
          default: return '';
        }
      };

      let direction = 'asc';

      switch (type) {
        case 'farm':
          this.pondfarmAsc = !this.pondfarmAsc;
          direction = this.pondfarmAsc ? 'asc' : 'desc';
          this.pondsectionnameAsc = this.pondlocationnameAsc = this.pondnameAsc = false;
          break;
        case 'sectionname':
          this.pondsectionnameAsc = !this.pondsectionnameAsc;
          direction = this.pondsectionnameAsc ? 'asc' : 'desc';
          this.pondfarmAsc = this.pondlocationnameAsc = this.pondnameAsc = false;
          break;
        case 'pondname':
          this.pondnameAsc = !this.pondnameAsc;
          direction = this.pondnameAsc ? 'asc' : 'desc';
          this.pondfarmAsc = this.pondlocationnameAsc = this.pondsectionnameAsc = false;
          break;
        case 'locationname':
          this.pondlocationnameAsc = !this.pondlocationnameAsc;
          direction = this.pondlocationnameAsc ? 'asc' : 'desc';
          this.pondfarmAsc = this.pondnameAsc = this.pondsectionnameAsc = false;
          break;
      }

      this.customerPondsList = [...sortblelist].sort((a, b) => {
        const valA = getValue(a).toLowerCase();
        const valB = getValue(b).toLowerCase();
        if (valA < valB) return direction === 'asc' ? -1 : 1;
        if (valA > valB) return direction === 'asc' ? 1 : -1;
        return 0;
      });

      // Refresh visible data after sorting
      this.batchLoader?.reset();
      this.batchLoader?.loadNextBatch(true);

    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

}
